import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import type {
  ItineraryUpdate,
  EnrichmentCompleteEvent,
  HotelSearchResultsData,
  ExperienceSearchResultsData,
  HotelSearchErrorData,
  ExperienceSearchErrorData,
  ChatUiActionData,
  ChatMetadataUpdatedData,
} from 'tripPlanner/api/types'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import { useChatMessages } from './useChatMessages'
import { useChatScroll, type ChatSnapPoint } from './useChatScroll'
import { useStreamMachine, EMPTY_SUMMARIES, type ChatLoadingState } from './useStreamMachine'
import { useStreamEventProcessor } from './useStreamEventProcessor'
import { useStreamConnection } from './useStreamConnection'
import { getTextFromParts } from 'tripPlanner/utils/messageParts'
import type { UserData } from '../../types'
export type { UserData }

export interface UseChatCoreArgs {
  chatId: number | undefined
  setChatId: (id: number | undefined) => void
  prompt: string | undefined
  isDevMode: boolean
  files: Array<File>
  setFiles: (files: Array<File>) => void
  isLoggedIn: boolean
  orderIds?: Array<string>
  // Callbacks
  refetchChatList: () => Promise<unknown>
  startChatTitleGeneration: (chatId: number, message: string) => void
  updateChatLastActivityTimestamp: (chatId: number, iso: string) => void
  updateChatMetadata: (chatId: number, name: string, updatedAt: string) => void
  onItineraryUpdates?: (chatId: number, updates: Array<ItineraryUpdate>) => void
  onEnrichmentComplete?: (chatId: number, event: EnrichmentCompleteEvent) => void
  onStreamComplete?: (chatId: number) => void
  onHotelSearchResults?: (data: HotelSearchResultsData, generation: number) => void
  onExperienceSearchResults?: (data: ExperienceSearchResultsData, generation: number) => void
  onHotelSearchError?: (data: HotelSearchErrorData, generation: number) => void
  onExperienceSearchError?: (data: ExperienceSearchErrorData, generation: number) => void
  onUiAction?: (data: ChatUiActionData) => void
  // Refs
  messagesWrapperRef: React.RefObject<HTMLDivElement | null>
  userData: UserData
  updateItineraryWithTools?: boolean
  // Scroll config
  chatKey: string
  chatSnapPoint?: ChatSnapPoint
  shouldUseMobileOverlay?: boolean
  isViewingHistory?: boolean
  activeHistoryIndex?: number | undefined
}

// Output from useChatCore - clean, minimal API
export interface UseChatCoreReturn {
  // Messages (from useChatMessages)
  messages: Array<MessageItem>
  filteredMessages: Array<MessageItem>
  userFeedbackMap: Record<string, 'UP' | 'DOWN'>
  setUserFeedbackMap: React.Dispatch<React.SetStateAction<Record<string, 'UP' | 'DOWN'>>>
  isChatError: boolean
  chatError: unknown
  loadingState: ChatLoadingState
  isStreaming: boolean
  /** True while the HTTP response body is still being consumed (covers post-content_complete enrichment). */
  isStreamOpen: boolean
  /** True from first content until MESSAGE_END applies final parts (covers the CONTENT_COMPLETE → MESSAGE_END gap). */
  isAwaitingMessageEnd: boolean

  // Streaming controls (from useStreamConnection)
  submitMessage: (message: string, options?: { messageType?: string }) => Promise<void>
  regenerateResponse: () => Promise<void>
  stopStreaming: () => Promise<boolean>
  retryLastMessage: () => Promise<void>

  // Scroll controls (from useChatScroll)
  handleScroll: () => void
  scrollToBottom: (force?: boolean) => void
  isAtBottom: boolean
  followTail: boolean
  hasContentOutOfView: boolean
  enableFollowAndScrollToBottom: () => void
  setFollowTail: React.Dispatch<React.SetStateAction<boolean>>
  scrollToMessage: (messageId: string | undefined) => boolean
  dynamicSpacerHeight: number
  setDynamicSpacerHeight: React.Dispatch<React.SetStateAction<number>>

  // Combined reset - followTail defaults to true (for streaming) but can be set to false when starting fresh
  resetConversation: (options?: { followTail?: boolean }) => void
  resetStreamingState: () => void

  // Machine context for inline retry (G4) and stalled detection
  streamError: Error | undefined
  isStalled: boolean
  reasoningSummaries: ReadonlyArray<string>
  toolCallInfo: { tool: string; args: Record<string, unknown> } | undefined

  // Internal refs needed by ChatTripPlannerPage for edge cases
  latestGenerationRef: React.MutableRefObject<number>
}

export function useChatCore({
  chatId,
  setChatId,
  prompt,
  isDevMode,
  files,
  setFiles,
  isLoggedIn,
  orderIds,
  refetchChatList,
  startChatTitleGeneration,
  updateChatLastActivityTimestamp,
  updateChatMetadata,
  onItineraryUpdates,
  onEnrichmentComplete,
  onStreamComplete,
  onHotelSearchResults,
  onExperienceSearchResults,
  onHotelSearchError,
  onExperienceSearchError,
  onUiAction,
  messagesWrapperRef,
  userData,
  updateItineraryWithTools,
  chatKey,
  chatSnapPoint = 'expanded',
  shouldUseMobileOverlay = false,
  isViewingHistory = false,
  activeHistoryIndex = undefined,
}: UseChatCoreArgs): UseChatCoreReturn {
  const intl = useIntl()
  const latestGenerationRef = useRef<number>(0)
  const streamingMessageIdRef = useRef<string | undefined>(undefined)
  const chatIdRef = useRef(chatId)
  chatIdRef.current = chatId

  // ── XState streaming machine (single source of truth for all loading state) ──

  const {
    send,
    getSnapshot,
    phase: machinePhase,
    context: machineContext,
    loadingState,
    isStreaming,
    isStreamOpen,
    isAwaitingMessageEnd,
    isStalled,
  } = useStreamMachine()

  // ── Messages ──

  const {
    messages,
    filteredMessages,
    setPendingUserMessage,
    setStreamingMessage,
    updateStreamingMessageParts,
    updateStreamingMessageMetadata,
    invalidateChat,
    isQueryLoading,
    isChatError,
    chatError,
    userFeedbackMap,
    setUserFeedbackMap,
    resetMessages,
    pendingUserMessage,
    messagesReady,
  } = useChatMessages({ chatId, isDevMode })

  // Refs for the resume effect — avoid stale closures without adding deps that cause re-fires
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  // messages is for render reads (regenerate lookup); messagesRef is for effect reads (resume)

  // v3: derive isStreaming from both XState state AND server generationStatus.
  let latestAssistantMessage: MessageItem | undefined
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') { latestAssistantMessage = messages[i]; break }
  }
  const serverIsStreaming = latestAssistantMessage?.generationStatus === 'PENDING' || latestAssistantMessage?.generationStatus === 'IN_PROGRESS'
  const isStreamingCombined = isStreaming || serverIsStreaming

  // isStreamingRef reflects XState machine state only (not server-derived).
  // The resume effect uses this to check "is the machine already running a stream?"
  // not "does the server have an in-flight row?" — the latter is what triggers resume.
  const isStreamingRef = useRef(isStreaming)
  isStreamingRef.current = isStreaming

  // ── Scroll ──

  const isRegenerating = isStreaming && !pendingUserMessage

  const {
    handleScroll,
    scrollToBottom,
    isAtBottom,
    followTail,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    setFollowTail,
    scrollToMessage,
    dynamicSpacerHeight,
    setDynamicSpacerHeight,
    scrollToNewMessage,
    recalculateSpacerAfterStream,
  } = useChatScroll({
    messagesWrapperRef,
    isStreaming,
    isRegenerating,
    messages: filteredMessages,
    chatKey,
    chatSnapPoint,
    shouldUseMobileOverlay,
    isViewingHistory,
    hasMessages: filteredMessages.length > 0,
    activeHistoryIndex,
  })

  // ── Event processor ──

  const isLatestGeneration = useCallback((generation: number) => {
    return generation >= latestGenerationRef.current
  }, [latestGenerationRef])

  const handleChatMetadataUpdated = useCallback((data: ChatMetadataUpdatedData) => {
    updateChatMetadata(data.chatId, data.name, data.updatedAt)
  }, [updateChatMetadata])

  const { createHandlers, resetBuffer } = useStreamEventProcessor({
    send,
    updateStreamingMessageParts,
    updateStreamingMessageMetadata,
    onItineraryUpdates,
    onEnrichmentComplete,
    onHotelSearchResults,
    onExperienceSearchResults,
    onHotelSearchError,
    onExperienceSearchError,
    onUiAction,
    onChatMetadataUpdated: handleChatMetadataUpdated,
    isLatestGeneration,
    chatIdRef,
  })

  // ── Stream connection ──

  const {
    handleSubmit: streamingSubmit,
    handleRegenerateResponse: streamingRegenerate,
    handleStopStreaming,
    handleRetry,
    resetStreamingState,
  } = useStreamConnection({
    send,
    setPendingUserMessage,
    setStreamingMessage,
    invalidateChat,
    createHandlers,
    resetBuffer,
    scrollToNewMessage,
    recalculateSpacerAfterStream,
    refetchChatList,
    startChatTitleGeneration,
    updateChatLastActivityTimestamp,
    onStreamComplete,
    chatId,
    setChatId,
    prompt,
    isLoggedIn,
    isStreaming,
    orderIds,
    files,
    setFiles,
    userData,
    updateItineraryWithTools,
    messages,
    messagesRef,
    isStreamingRef,
    messagesReady,
    latestGenerationRef,
    streamingMessageIdRef,
  })

  // ── Inline retry (G4: reuse message IDs for server idempotency) ──

  const retryLastMessage = useCallback(async() => {
    // Read fresh state at call time (not from render closure) to avoid stale
    // phase/context if the machine transitions between render and button click.
    const { phase, context } = getSnapshot()
    if (phase !== 'error') return

    const { chatId: errorChatId, userMessageId, assistantMessageId } = context
    if (errorChatId === undefined || !userMessageId || !assistantMessageId) return

    // Find the user message content from messages or pending overlay
    const userMsg = messages.find(m => m.id === userMessageId) ??
      (pendingUserMessage?.id === userMessageId ? pendingUserMessage : undefined)
    if (!userMsg) return

    const content = getTextFromParts(userMsg.parts)
    await handleRetry({ chatId: errorChatId, userMessageId, assistantMessageId, content })
  }, [getSnapshot, messages, pendingUserMessage, handleRetry])

  // ── History loading ──

  useEffect(() => {
    if (isQueryLoading && chatId !== undefined && loadingState.status !== 'loadingHistory') {
      send({ type: 'LOAD_HISTORY_START' })
    } else if (!isQueryLoading && loadingState.status === 'loadingHistory') {
      if (isChatError && chatError) {
        const errorMessage = chatError instanceof Error ? chatError.message : intl.formatMessage({
          id: 'ILWx2vsWqE0cW0nZCQ4X',
          defaultMessage: 'Failed to load chat',
          description: 'Fallback error message when loading chat history fails with a non-Error object',
        })
        send({ type: 'LOAD_HISTORY_ERROR', message: errorMessage })
      } else {
        send({ type: 'LOAD_HISTORY_SUCCESS' })
      }
    }
  }, [isQueryLoading, chatId, isChatError, chatError, loadingState.status, send, intl])

  const resetConversation = useCallback((options?: { followTail?: boolean }) => {
    resetMessages()
    resetStreamingState()
    setFollowTail(options?.followTail ?? true)
  }, [resetMessages, resetStreamingState, setFollowTail])

  return {
    messages,
    filteredMessages,
    userFeedbackMap,
    setUserFeedbackMap,
    isChatError,
    chatError,
    loadingState,
    isStreaming: isStreamingCombined,
    isStreamOpen,
    isAwaitingMessageEnd,
    submitMessage: streamingSubmit,
    regenerateResponse: streamingRegenerate,
    stopStreaming: handleStopStreaming,
    retryLastMessage,
    handleScroll,
    scrollToBottom,
    isAtBottom,
    followTail,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    setFollowTail,
    scrollToMessage,
    dynamicSpacerHeight,
    setDynamicSpacerHeight,
    resetConversation,
    resetStreamingState,
    streamError: machinePhase === 'error' ? machineContext.error : undefined,
    isStalled,
    reasoningSummaries: loadingState.status === 'reasoning' ? machineContext.reasoningSummaries : EMPTY_SUMMARIES,
    toolCallInfo: loadingState.status === 'toolCalling' ? machineContext.toolCallInfo : undefined,
    latestGenerationRef,
  }
}
